/**
 * useCockpitBreakpoint — Viewport-detection hook for the Inbox Cockpit.
 *
 * Returns one of: 'desktop' | 'tablet' | 'narrow-tablet' | 'mobile'
 * SSR/no-window safe: returns 'desktop' when window is undefined.
 *
 * Authority: WI-UI-CLEANUP-001 Stage 3, SA-2 §1.
 * Design: docs/agents/pitch-deck-review/pages/DEAL_EVALUATOR_PAGE_SPEC_V2.md §4.1
 */

import { useEffect, useState } from 'react';

const BREAKPOINTS = {
  desktop: '(min-width: 1280px)',
  tablet: '(min-width: 960px) and (max-width: 1279.98px)',
  narrowTablet: '(min-width: 640px) and (max-width: 959.98px)',
  mobile: '(max-width: 639.98px)',
};

function detect() {
  if (typeof window === 'undefined' || !window.matchMedia) return 'desktop';
  if (window.matchMedia(BREAKPOINTS.desktop).matches) return 'desktop';
  if (window.matchMedia(BREAKPOINTS.tablet).matches) return 'tablet';
  if (window.matchMedia(BREAKPOINTS.narrowTablet).matches) return 'narrow-tablet';
  return 'mobile';
}

export function useCockpitBreakpoint() {
  const [bp, setBp] = useState(detect);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const handler = () => setBp(detect());
    const mqls = Object.values(BREAKPOINTS).map((q) => window.matchMedia(q));
    mqls.forEach((m) =>
      m.addEventListener ? m.addEventListener('change', handler) : m.addListener(handler)
    );
    return () => {
      mqls.forEach((m) =>
        m.removeEventListener ? m.removeEventListener('change', handler) : m.removeListener(handler)
      );
    };
  }, []);
  return bp;
}

export default useCockpitBreakpoint;
